import { useState } from "react"

import { createStandaloneToast } from "@chakra-ui/react"

import superjson from "superjson"
import type { InvalidateQueryFilters } from "@tanstack/react-query"
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query"
import type { TRPCClientErrorLike } from "@trpc/client"
import {
  createTRPCClient,
  httpBatchLink,
  isTRPCClientError,
} from "@trpc/client"
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server"
import { createTRPCContext } from "@trpc/tanstack-react-query"

// ! Important: Import only the AppRouter type from the api package
import type { AdminRouter } from "@supliful/api"
import { parseCookies } from "@supliful/common"

import { API_HOST, defaultChakraToastOptions, SESSION_TOKEN } from "./config"

type AdminTRPCError = TRPCClientErrorLike<AdminRouter>

interface MutationMeta extends Record<string, unknown> {
  successMessage?: string
  errorMessage?: string | ((error: AdminTRPCError) => string)
  invalidatesQuery?: InvalidateQueryFilters<any[]>
}
declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: MutationMeta
  }
}

export const TRPC_URL = `${API_HOST}/trpc/admin`
const trpc = createTRPCClient<AdminRouter>({
  links: [
    httpBatchLink({
      url: TRPC_URL,
      transformer: superjson,
      headers() {
        const token = parseCookies()[SESSION_TOKEN]
        return {
          Authorization: token ? `Bearer ${token}` : "",
        }
      },
      async fetch(url, options) {
        const response = await fetch(url, options)

        // Handle server errors that return non-JSON responses (e.g., 502, 503, 504)
        // These typically return HTML/plain text which causes JSON parsing errors
        if (response.status >= 500) {
          const contentType = response.headers.get("content-type")

          if (!contentType?.includes("application/json")) {
            // tRPC error response format
            const errorResponse = [
              {
                error: {
                  json: {
                    message: "Server error. Please try again later.",
                    code: -32603, // JSON-RPC internal error
                    data: {
                      code: "INTERNAL_SERVER_ERROR",
                      httpStatus: response.status,
                    },
                  },
                },
              },
            ]

            return new Response(JSON.stringify(errorResponse), {
              headers: { "Content-Type": "application/json" },
            })
          }
        }

        return response
      },
    }),
  ],
})

export const { TRPCProvider, useTRPC } = createTRPCContext<AdminRouter>()

const { toast } = createStandaloneToast({
  defaultOptions: defaultChakraToastOptions,
})
function makeQueryClient(onUnauthorized?: () => void) {
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        // Handle UNAUTHORIZED errors globally for queries
        if (isUnauthorized(error)) {
          onUnauthorized?.()
        }
      },
    }),
    mutationCache: new MutationCache({
      onSuccess(_data, _variables, _context, mutation) {
        if (mutation.meta?.successMessage) {
          toast({ status: "success", title: mutation.meta?.successMessage })
        }
      },
      onError(error, _variables, _context, mutation) {
        // Handle UNAUTHORIZED errors globally for mutations
        if (isUnauthorized(error)) {
          onUnauthorized?.()
        }
        if (mutation.meta?.errorMessage) {
          if (
            isTRPCClientError<AdminRouter>(error) &&
            typeof mutation.meta.errorMessage === "function"
          ) {
            const message = mutation.meta.errorMessage(error)
            toast({ status: "error", title: message })
          }
          if (typeof mutation.meta.errorMessage === "string") {
            toast({ status: "error", title: mutation.meta.errorMessage })
          }
        }

        // Safe to log for admin users
        console.error(error)
      },
      onSettled(_data, _error, _variables, _context, mutation) {
        if (mutation.meta?.invalidatesQuery) {
          queryClient.invalidateQueries(mutation.meta.invalidatesQuery)
        }
      },
    }),
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 5 * 1000,
      },
    },
  })
  return queryClient
}
let browserQueryClient: QueryClient | undefined = undefined

export function getQueryClient(onUnauthorized?: () => void) {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return makeQueryClient()
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient)
      browserQueryClient = makeQueryClient(onUnauthorized)
    return browserQueryClient
  }
}

export function useTrpcClient() {
  const [trpcClient] = useState(() => trpc)

  return trpcClient
}

type RouterInput = inferRouterInputs<AdminRouter>
type RouterOutput = inferRouterOutputs<AdminRouter>

export type { RouterInput, RouterOutput }

/**
 * Utility function to check if any error should trigger logout
 */
function isUnauthorized(error: unknown): boolean {
  if (!isTRPCClientError<AdminRouter>(error)) {
    return false
  }

  return error.data?.code === "UNAUTHORIZED"
}
